import { falArrowDownWideShort } from 'shared/@generated/icons/falArrowDownWideShort';
import { falCheck } from 'shared/@generated/icons/falCheck';
import { falSlidersSimple } from 'shared/@generated/icons/falSlidersSimple';
import { useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Animal, BreedType, Characteristic } from 'shared/@generated/models';
import { BaseModalRef } from 'shared/components/base-modal';
import { CharacteristicBadge } from 'shared/components/characteristic-badge';
import { DropdownSelect } from 'shared/components/form/dropdown-select';
import { TextInput } from 'shared/components/form/text-input';
import { Icon } from 'shared/components/icon';
import { Text } from 'shared/components/text';
import {
  ActiveFilterItem,
  ActiveFilterList,
  FilterButton,
  FilterButtonRow,
  FilterModal,
  FilterModalFooter,
  FilterOption,
  FilterOverviewRow,
  SelectFilterPanel,
} from 'shared/components/web-filter-controls';
import {
  type LockedBreedsFilter,
  sortOptions,
  useBreedsFilterStore,
} from 'shared/state/breeds-filter';

type FilterPanel = 'overview' | 'query' | 'animal' | 'breedType' | 'size' | 'characteristics';

export function BreedsWebFilterControls({
  totalBreeds,
  lockedFilter,
  onClear,
}: {
  totalBreeds: number;
  lockedFilter?: LockedBreedsFilter;
  onClear: () => void;
}) {
  const { t } = useTranslation('components');
  const filter = useBreedsFilterStore((state) => state.filter);
  const setFilter = useBreedsFilterStore((state) => state.setFilter);
  const modalRef = useRef<BaseModalRef>(null);
  const [panel, setPanel] = useState<FilterPanel>('overview');
  const animalLocked = lockedFilter?.animal !== undefined;
  const sizeLocked = lockedFilter?.size !== undefined;

  const animalOptions: FilterOption<Animal>[] = [
    { label: t('breeds_filter_form.all_animals'), value: null },
    ...Object.values(Animal).map((value) => ({
      label: t(`common:enums.animal.${value}`),
      value,
    })),
  ];
  const breedTypeOptions: FilterOption<BreedType>[] = [
    { label: t('breeds_filter_form.all_breed_types'), value: null },
    ...Object.values(BreedType)
      .filter((value) => value !== BreedType.MixedBreed)
      .map((value) => ({
        label: t(`common:enums.breed_type.${value}`),
        value,
      })),
  ];
  const sizeOptions: FilterOption<string>[] = [
    { label: t('breeds_filter_form.all_sizes'), value: null },
    ...Object.values(Characteristic)
      .filter((value) => value.startsWith('size_'))
      .map((value) => ({
        label: t(`breed_badge.${value}`),
        value,
      })),
  ];
  const characteristicOptions = Object.values(Characteristic).filter(
    (value) => !value.startsWith('size_'),
  );

  const selectedCharacteristics = filter.characteristics ?? [];
  const selectedLabels = {
    query: filter.query.trim() || t('breeds_filter_form.all_names'),
    animal: selectedOptionLabel(animalOptions, filter.animal),
    breedType: selectedOptionLabel(breedTypeOptions, filter.breedType),
    size: selectedOptionLabel(sizeOptions, filter.size),
    characteristics:
      selectedCharacteristics.length > 0
        ? selectedCharacteristics.map((value) => t(`breed_badge.${value}`)).join(', ')
        : t('breeds_filter_form.all_characteristics'),
  };
  const activeCategoryCount = [
    filter.query.trim(),
    !animalLocked && filter.animal,
    filter.breedType,
    !sizeLocked && filter.size,
    selectedCharacteristics.length > 0,
  ].filter(Boolean).length;
  const hasClearableFilters =
    activeCategoryCount > 0 || Boolean(filter.query.trim()) || Boolean(filter.sortBy);

  function openPanel(nextPanel: FilterPanel) {
    setPanel(nextPanel);
    modalRef.current?.open();
  }

  function setCharacteristics(values: Characteristic[]) {
    setFilter({ characteristics: values.length > 0 ? values : null });
  }

  function toggleCharacteristic(characteristic: Characteristic) {
    setCharacteristics(
      selectedCharacteristics.includes(characteristic)
        ? selectedCharacteristics.filter((value) => value !== characteristic)
        : [...selectedCharacteristics, characteristic],
    );
  }

  const activeFilters: ActiveFilterItem[] = [
    ...(filter.query.trim()
      ? [
          {
            key: 'query',
            label: filter.query.trim(),
            onRemove: () => setFilter({ query: '' }),
          },
        ]
      : []),
    ...(!animalLocked && filter.animal
      ? [
          {
            key: 'animal',
            label: selectedLabels.animal,
            onRemove: () => setFilter({ animal: null }),
          },
        ]
      : []),
    ...(filter.breedType
      ? [
          {
            key: 'breed-type',
            label: selectedLabels.breedType,
            onRemove: () => setFilter({ breedType: null }),
          },
        ]
      : []),
    ...(!sizeLocked && filter.size
      ? [
          {
            key: 'size',
            label: selectedLabels.size,
            onRemove: () => setFilter({ size: null }),
          },
        ]
      : []),
    ...selectedCharacteristics.map((characteristic) => ({
      key: `characteristic-${characteristic}`,
      label: t(`breed_badge.${characteristic}`),
      onRemove: () =>
        setCharacteristics(selectedCharacteristics.filter((value) => value !== characteristic)),
    })),
  ];

  const modalTitle =
    panel === 'overview'
      ? t('breeds_filter_form.filter_breeds')
      : {
          query: t('breeds_filter_form.name'),
          animal: t('breeds_filter_form.animal'),
          breedType: t('breeds_filter_form.breed_type'),
          size: t('breeds_filter_form.size'),
          characteristics: t('breeds_filter_form.characteristics_title'),
        }[panel];

  return (
    <View className={'mt-3 md:mt-4'}>
      <FilterButtonRow>
        <FilterButton
          label={t('breeds_filter_form.all_filters')}
          icon={falSlidersSimple}
          count={activeCategoryCount}
          active={activeCategoryCount > 0}
          testID={'breeds-filter-trigger'}
          onPress={() => openPanel('overview')}
        />
        <FilterButton
          label={t('breeds_filter_form.name')}
          count={filter.query.trim() ? 1 : 0}
          active={Boolean(filter.query.trim())}
          testID={'breeds-filter-query'}
          onPress={() => openPanel('query')}
        />
        {!animalLocked && (
          <FilterButton
            label={t('breeds_filter_form.animal')}
            count={filter.animal ? 1 : 0}
            active={Boolean(filter.animal)}
            onPress={() => openPanel('animal')}
          />
        )}
        <FilterButton
          label={t('breeds_filter_form.breed_type')}
          count={filter.breedType ? 1 : 0}
          active={Boolean(filter.breedType)}
          onPress={() => openPanel('breedType')}
        />
        {!sizeLocked && (
          <FilterButton
            label={t('breeds_filter_form.size')}
            count={filter.size ? 1 : 0}
            active={Boolean(filter.size)}
            onPress={() => openPanel('size')}
          />
        )}
        <FilterButton
          label={t('breeds_filter_form.characteristics')}
          count={selectedCharacteristics.length}
          active={selectedCharacteristics.length > 0}
          onPress={() => openPanel('characteristics')}
        />
      </FilterButtonRow>

      <ActiveFilterList items={activeFilters} clearLabel={t('common:clear')} onClear={onClear} />

      <View className={'mt-3 flex-row items-center gap-2 pb-2 md:gap-3 md:py-2'}>
        <Text
          className={'text-brand-brown-dark min-w-0 flex-1 text-sm font-semibold md:text-base'}
          accessibilityLiveRegion={'polite'}
          numberOfLines={1}
        >
          {t('breeds_filter_form.matches_found', { count: totalBreeds })}
        </Text>

        <DropdownSelect
          title={t('breeds_filter_form.sort.recommended')}
          options={[
            {
              label: t('breeds_filter_form.sort.recommended'),
              value: null,
            },
            ...sortOptions.map((value) => ({
              label: t(`breeds_filter_form.sort.${value}`),
              value,
            })),
          ]}
          value={filter.sortBy}
          dense={true}
          bold={true}
          contentAlign={'end'}
          triggerClassName={'flex-none'}
          triggerIcon={falArrowDownWideShort}
          onChange={(sortBy) => setFilter({ sortBy })}
        />
      </View>

      <FilterModal
        ref={modalRef}
        title={modalTitle}
        overview={panel === 'overview'}
        backLabel={t('breeds_filter_form.all_filters')}
        onBack={() => setPanel('overview')}
        contentTestID={'breeds-filter-modal'}
        footer={({ close }) => (
          <FilterModalFooter
            clearLabel={t('common:clear')}
            applyLabel={t('breeds_filter_form.view_breeds')}
            resultCount={totalBreeds}
            clearDisabled={!hasClearableFilters}
            clearTestID={'breeds-filter-clear'}
            applyTestID={'breeds-filter-apply'}
            onClear={onClear}
            onApply={close}
          />
        )}
      >
        <View className={'min-h-0 flex-1'}>
          {panel !== 'overview' && (
            <Text className={'sr-only'}>{t('breeds_filter_form.filter_breeds')}</Text>
          )}
          {panel === 'overview' ? (
            <ScrollView contentContainerClassName={'p-3 md:px-5 md:py-4'}>
              <FilterOverviewRow
                label={t('breeds_filter_form.name')}
                value={selectedLabels.query}
                active={Boolean(filter.query.trim())}
                onPress={() => setPanel('query')}
              />
              {!animalLocked && (
                <FilterOverviewRow
                  label={t('breeds_filter_form.animal')}
                  value={selectedLabels.animal}
                  active={Boolean(filter.animal)}
                  testID={'breeds-filter-animal'}
                  onPress={() => setPanel('animal')}
                />
              )}
              <FilterOverviewRow
                label={t('breeds_filter_form.breed_type')}
                value={selectedLabels.breedType}
                active={Boolean(filter.breedType)}
                onPress={() => setPanel('breedType')}
              />
              {!sizeLocked && (
                <FilterOverviewRow
                  label={t('breeds_filter_form.size')}
                  value={selectedLabels.size}
                  active={Boolean(filter.size)}
                  onPress={() => setPanel('size')}
                />
              )}
              <FilterOverviewRow
                label={t('breeds_filter_form.characteristics')}
                value={selectedLabels.characteristics}
                active={selectedCharacteristics.length > 0}
                onPress={() => setPanel('characteristics')}
              />
            </ScrollView>
          ) : (
            <View className={'min-h-0 flex-1'}>
              {panel === 'query' && (
                <View className={'px-3 py-5 md:px-5'}>
                  <TextInput
                    dense={true}
                    autoFocus={true}
                    placeholder={t('breeds_filter_form.search_by_name')}
                    value={filter.query}
                    onChangeText={(query: string) => setFilter({ query })}
                  />
                </View>
              )}
              {panel === 'animal' && (
                <SelectFilterPanel
                  options={animalOptions}
                  value={filter.animal}
                  onChange={(animal) => setFilter({ animal })}
                />
              )}
              {panel === 'breedType' && (
                <SelectFilterPanel
                  options={breedTypeOptions}
                  value={filter.breedType}
                  onChange={(breedType) => setFilter({ breedType })}
                />
              )}
              {panel === 'size' && (
                <SelectFilterPanel
                  options={sizeOptions}
                  value={filter.size}
                  onChange={(size) => setFilter({ size })}
                />
              )}
              {panel === 'characteristics' && (
                <CharacteristicsFilterPanel
                  options={characteristicOptions}
                  selected={selectedCharacteristics}
                  onToggle={toggleCharacteristic}
                />
              )}
            </View>
          )}
        </View>
      </FilterModal>
    </View>
  );
}

function selectedOptionLabel<T>(options: FilterOption<T>[], value: T | null) {
  return options.find((option) => option.value === value)?.label ?? options[0]!.label;
}

function CharacteristicsFilterPanel({
  options,
  selected,
  onToggle,
}: {
  options: Characteristic[];
  selected: Characteristic[];
  onToggle: (value: Characteristic) => void;
}) {
  return (
    <ScrollView contentContainerClassName={'px-3 pb-6 md:px-5'}>
      {options.map((characteristic) => {
        const checked = selected.includes(characteristic);

        return (
          <Pressable
            key={characteristic}
            className={
              'hover:bg-brand-cream -mx-2 flex-row items-center gap-3 rounded-lg px-2 py-3 transition-colors'
            }
            aria-checked={checked}
            accessibilityRole={'checkbox'}
            accessibilityState={{ checked }}
            onPress={() => onToggle(characteristic)}
          >
            <View className={'min-w-0 flex-1 origin-left scale-90'}>
              <CharacteristicBadge characteristic={characteristic} />
            </View>
            <View
              className={`size-5 items-center justify-center rounded border ${
                checked
                  ? 'border-brand-brown-dark bg-brand-brown-dark'
                  : 'border-brand-brown-dark/30 bg-white'
              }`}
            >
              {checked && <Icon icon={falCheck} className={'size-3 text-white'} />}
            </View>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}
